'use client'

import CurrencySelectorMobile from '@/app/components/LanguageAndCurrencyMobile/CurrencySelectorMobile'
import LanguageAndCurrencyPopup from '@/app/components/LanguageAndCurrencyMobile/LanguageAndCurrencyPopup'
import SelectorButton from '@/app/components/LanguageAndCurrencyMobile/SelectorButton'
import { Icons } from '@/app/components/ui/Icons'
import { INIT_DATA_STORAGE_KEY, TOP_CURRENCIES } from '@/app/constants'
import { getCurrencies } from '@/app/currencies'
import { getLanguages, Language } from '@/app/languages'
import { Currency, DestPagesProps, UserData } from '@/app/types'
import { useLocalStorage } from '@uidotdev/usehooks'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { FC, useMemo, useState } from 'react'

type LanguageAndCurrencySelectProps = {
	onClose?: () => void
	className?: string
}

const LanguageAndCurrencySelect: FC<LanguageAndCurrencySelectProps> = ({
	onClose,
	className,
}) => {
	const router = useRouter()
	const pathname = usePathname()
	const t = useTranslations('Header')
	const params = useParams<DestPagesProps['params']>()
	const currentLanguage = params.lang
	const [initData, setInitData] = useLocalStorage<UserData>(
		INIT_DATA_STORAGE_KEY
	)
	const defaultCurrency = initData?.defaultCurrency || TOP_CURRENCIES[0]
	const userCurrency = initData?.selectedCurrency || defaultCurrency

	const [isOpen, setIsOpen] = useState(false)
	const [isLanguageSelectOpen, setIsLanguageSelectOpen] = useState(false)
	const [isCurrencySelectOpen, setIsCurrencySelectOpen] = useState(false)

	const [currentLanguageObj, setCurrentLanguageObj] = useState<Language | null>(
		null
	)
	const [currentCurrency, setCurrentCurrency] = useState<Currency>(userCurrency)

	const [tempLanguage, setTempLanguage] = useState<Language | null>(null)
	const [tempCurrency, setTempCurrency] = useState<Currency>(userCurrency)

	const languages = getLanguages()
	const allCurrencies = getCurrencies()

	const currencies = useMemo(() => {
		const allCurrenciesSet = new Set(allCurrencies.map((c) => c.abbreviation))

		let result = [...allCurrencies]

		if (!allCurrenciesSet.has(defaultCurrency.abbreviation)) {
			result = [defaultCurrency, ...result]
		}

		if (
			userCurrency.abbreviation !== defaultCurrency.abbreviation &&
			!allCurrenciesSet.has(userCurrency.abbreviation)
		) {
			result = [userCurrency, ...result]
		}

		return result
	}, [allCurrencies, userCurrency, defaultCurrency])

	const handleLocaleChange = (language: Language) => {
		setTempLanguage(language)
		setTimeout(() => {
			setIsLanguageSelectOpen(false)
		}, 300)
	}

	const handleCurrencyChange = (currency: Currency) => {
		setTempCurrency(currency)
		setTimeout(() => {
			setIsCurrencySelectOpen(false)
		}, 300)
	}

	const handleSave = () => {
		let newPath = pathname
		let shouldNavigate = false

		if (tempLanguage && tempLanguage.code !== currentLanguage) {
			newPath = pathname.replace(`/${currentLanguage}`, `/${tempLanguage.code}`)
			setCurrentLanguageObj(tempLanguage)
			shouldNavigate = true
		}

		if (
			tempCurrency &&
			tempCurrency.abbreviation !== currentCurrency.abbreviation
		) {
			setCurrentCurrency(tempCurrency)
			setInitData({
				...initData,
				selectedCurrency: tempCurrency,
			})

			const url = new URL(window.location.href)
			url.searchParams.set('currency', tempCurrency.abbreviation)

			if (shouldNavigate) {
				const urlObj = new URL(`${window.location.origin}${newPath}`)
				urlObj.searchParams.set('currency', tempCurrency.abbreviation)
				newPath = `${urlObj.pathname}${urlObj.search}`
			} else {
				newPath = `${pathname}${url.search}`
				shouldNavigate = true
			}
		}

		if (shouldNavigate) {
			router.push(newPath)
		}

		setIsOpen(false)
		if (onClose) onClose()
	}

	const handleOpenLanguageSelect = () => {
		setIsLanguageSelectOpen(!isLanguageSelectOpen)
		setIsCurrencySelectOpen(false)
	}

	const handleOpenCurrencySelect = () => {
		setIsCurrencySelectOpen(!isCurrencySelectOpen)
		setIsLanguageSelectOpen(false)
	}

	const handleClosePopup = () => {
		setIsOpen(false)
		if (onClose) onClose()
	}

	const handleOpenPopup = () => {
		const currentLang =
			currentLanguageObj ||
			languages.find((lang) => lang.code === currentLanguage) ||
			null

		setTempLanguage(currentLang)
		setTempCurrency(currentCurrency)
		setIsOpen(true)
	}

	const displayLanguage =
		tempLanguage ||
		currentLanguageObj ||
		languages.find((lang) => lang.code === currentLanguage)
	const displayCurrency = tempCurrency

	return (
		<>
			<button
				onClick={handleOpenPopup}
				className={`flex items-center justify-end w-10 h-10 p-2 -m-2`}
				aria-label="Open language and currency selector"
			>
				<Icons.globeBig />
			</button>

			<LanguageAndCurrencyPopup
				isOpen={isOpen}
				onClose={handleClosePopup}
				onSave={handleSave}
			>
				<div className="flex flex-col w-full">
					{/* <SelectorButton
						flagCode={displayLanguage?.flag || 'us'}
						title={`${displayLanguage?.name} (${displayLanguage?.code.toUpperCase()})`}
						subtitle="Language"
						isOpen={isLanguageSelectOpen}
						onClick={handleOpenLanguageSelect}
						ariaControls="language-selector"
					/> */}
					{/* 
					<LanguageSelectorMobile
						isOpen={isLanguageSelectOpen}
						selectedLanguage={tempLanguage}
						languages={languages}
						onLanguageChange={handleLocaleChange}
					/> */}

					<SelectorButton
						flagCode={displayCurrency.flag}
						title={`${displayCurrency.label} (${displayCurrency.abbreviation})`}
						subtitle={t('currency')}
						isOpen={isCurrencySelectOpen}
						onClick={handleOpenCurrencySelect}
						ariaControls="currency-selector"
					/>

					<CurrencySelectorMobile
						isOpen={isCurrencySelectOpen}
						selectedCurrency={tempCurrency}
						currencies={currencies}
						onCurrencyChange={handleCurrencyChange}
					/>
				</div>
			</LanguageAndCurrencyPopup>
		</>
	)
}

export default dynamic(() => Promise.resolve(LanguageAndCurrencySelect), {
	ssr: false,
})
